import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import EditIcon from '@mui/icons-material/Edit';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import PageContainer from '../layout/PageContainer';
import { GET_CONSUMABLE_LINE_ITEMS_QUERY } from '../../queries/ConsumableLineItemQueries';
import { ConsumableLineItem, ConsumableLineItemUseType } from '../../models/ConsumableLineItem';
import {
  DataGrid,
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
  gridClasses
} from '@mui/x-data-grid';
import { toUSCurrency } from '../../utilities/CurrencyUtilities';

const INITIAL_PAGE_SIZE = 100;

export default function ConsumableShow() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: searchParams.get('page') ? Number(searchParams.get('page')) : 0,
    pageSize: searchParams.get('pageSize')
      ? Number(searchParams.get('pageSize'))
      : INITIAL_PAGE_SIZE,
  });

  const [filterModel, setFilterModel] = useState<GridFilterModel>(
    searchParams.get('filter')
      ? JSON.parse(searchParams.get('filter') ?? '')
      : { items: [] },
  );

  const [sortModel, setSortModel] = useState<GridSortModel>(
    searchParams.get('sort') ? JSON.parse(searchParams.get('sort') ?? '') : [],
  );

  const [yearlyConsumablesRowsState, setYearlyConsumablesRowsState] = useState<{
    rows: ConsumableLineItem[];
    rowCount: number;
  }>({
    rows: [],
    rowCount: 0,
  });

  const [batchConsumablesRowsState, setBatchConsumablesRowsState] = useState<{
    rows: ConsumableLineItem[];
    rowCount: number;
  }>({
    rows: [],
    rowCount: 0,
  });

  const { loading: yearlyConsumablesLoading, error: yearlyConsumablesError, data: yearlyConsumablesData } = useQuery(
    GET_CONSUMABLE_LINE_ITEMS_QUERY, {
    variables: {
      filter: { consumableLineItemUseType: { eq: ConsumableLineItemUseType.YEARLY } }
    },
    fetchPolicy: 'cache-and-network'
  });

  const { loading: batchConsumablesLoading, error: batchConsumablesError, data: batchConsumablesData } = useQuery(
    GET_CONSUMABLE_LINE_ITEMS_QUERY, {
    variables: {
      filter: { consumableLineItemUseType: { eq: ConsumableLineItemUseType.BATCH } }
    },
    fetchPolicy: 'cache-and-network'
  });

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      setPaginationModel(model);

      searchParams.set('page', String(model.page));
      searchParams.set('pageSize', String(model.pageSize));

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const handleFilterModelChange = useCallback(
    (model: GridFilterModel) => {
      setFilterModel(model);

      if (
        model.items.length > 0 ||
        (model.quickFilterValues && model.quickFilterValues.length > 0)
      ) {
        searchParams.set('filter', JSON.stringify(model));
      } else {
        searchParams.delete('filter');
      }

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      setSortModel(model);

      if (model.length > 0) {
        searchParams.set('sort', JSON.stringify(model));
      } else {
        searchParams.delete('sort');
      }

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const loadData = useCallback(async () => {
    setYearlyConsumablesRowsState({
      rows: yearlyConsumablesData?.consumableLineItems || [],
      rowCount: yearlyConsumablesData?.consumableLineItems.length || 0,
    });
    setBatchConsumablesRowsState({
      rows: batchConsumablesData?.consumableLineItems || [],
      rowCount: batchConsumablesData?.consumableLineItems.length || 0,
    });
  }, [yearlyConsumablesData?.consumableLineItems, batchConsumablesData?.consumableLineItems]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEdit = useCallback(() => {
    navigate(`/dashboard/consumables/edit`);
  }, [navigate]);

  const initialState = useMemo(
    () => ({
      pagination: { paginationModel: { pageSize: INITIAL_PAGE_SIZE } },
    }),
    [],
  );

  const yearlyConsumableCostColumns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'description',
        headerName: 'Description',
        width: 150
      },
      {
        field: 'orderQuantity',
        headerName: 'Quantity',
        type: 'number',
        width: 150
      },
      {
        field: 'unitQuantity',
        headerName: 'Unit Quantity',
        type: 'number',
        width: 150
      },
      {
        field: 'totalQuantity',
        headerName: 'Total Quantity',
        type: 'number',
        width: 150
      },
      {
        field: 'unitCost',
        headerName: 'Unit Cost ($)',
        type: 'number',
        width: 150,
        valueFormatter: (value: number) => toUSCurrency(value)
      },
      {
        field: 'subtotalCost',
        headerName: 'Subtotal ($)',
        type: 'number',
        width: 150,
        valueFormatter: (value: number) => toUSCurrency(value)
      },
      {
        field: 'unitTax',
        headerName: 'Tax ($)',
        type: 'number',
        width: 150,
        valueFormatter: (value: number) => toUSCurrency(value)
      },
      {
        field: 'shippingCost',
        headerName: 'Shipping Cost ($)',
        type: 'number',
        width: 150,
        valueFormatter: (value: number) => toUSCurrency(value)
      },
      {
        field: 'totalCost',
        headerName: 'Total Cost ($)',
        type: 'number',
        width: 150,
        valueFormatter: (value: number) => toUSCurrency(value)
      },
      {
        field: 'totalUnitCost',
        headerName: 'Total Unit Cost ($)',
        type: 'number',
        width: 150,
        valueFormatter: (value: number) => toUSCurrency(value)
      }
    ],
    [],
  );

  const batchConsumableCostColumns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'description',
        headerName: 'Description',
        width: 150
      },
      {
        field: 'orderQuantity',
        headerName: 'Quantity',
        type: 'number',
        width: 150
      },
      {
        field: 'unitQuantity',
        headerName: 'Unit Quantity',
        type: 'number',
        width: 150
      },
      {
        field: 'totalQuantity',
        headerName: 'Total Quantity',
        type: 'number',
        width: 150
      },
      {
        field: 'unitCost',
        headerName: 'Unit Cost ($)',
        type: 'number',
        width: 150,
        valueFormatter: (value: number) => toUSCurrency(value)
      },
      {
        field: 'subtotalCost',
        headerName: 'Subtotal ($)',
        type: 'number',
        width: 150,
        valueFormatter: (value: number) => toUSCurrency(value)
      },
      {
        field: 'unitTax',
        headerName: 'Tax ($)',
        type: 'number',
        width: 150,
        valueFormatter: (value: number) => toUSCurrency(value)
      },
      {
        field: 'shippingCost',
        headerName: 'Shipping Cost ($)',
        type: 'number',
        width: 150,
        valueFormatter: (value: number) => toUSCurrency(value)
      },
      {
        field: 'totalCost',
        headerName: 'Total Cost ($)',
        type: 'number',
        width: 150,
        valueFormatter: (value: number) => toUSCurrency(value)
      },
      {
        field: 'totalUnitCost',
        headerName: 'Total Unit Cost ($)',
        type: 'number',
        width: 150,
        valueFormatter: (value: number) => toUSCurrency(value)
      },
      {
        field: 'batchQuantity',
        headerName: 'Batch Quantity',
        type: 'number',
        width: 150
      },
      {
        field: 'totalBatchCost',
        headerName: 'Total Batch Cost ($)',
        type: 'number',
        width: 150,
        valueFormatter: (value: number) => toUSCurrency(value)
      },
    ],
    [],
  );

  const renderShow = useMemo(() => {
    if (yearlyConsumablesError) {
      return (
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="error">{yearlyConsumablesError.message}</Alert>
        </Box>
      );
    }

    if (batchConsumablesError) {
      return (
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="error">{batchConsumablesError.message}</Alert>
        </Box>
      );
    }

    return <>
      <Box sx={{ height: '27vh', width: '100%' }}>
        <DataGrid
          density="compact"
          label="Yearly Consumables"
          rows={yearlyConsumablesRowsState.rows}
          columns={yearlyConsumableCostColumns}
          pagination
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          disableRowSelectionOnClick
          loading={yearlyConsumablesLoading}
          initialState={initialState}
          showToolbar
          pageSizeOptions={[INITIAL_PAGE_SIZE]}
          sx={{
            [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
              outline: 'transparent',
            },
            [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
            {
              outline: 'none',
            },
            [`& .${gridClasses.row}:hover`]: {
              cursor: 'pointer',
            }
          }}
          slotProps={{
            loadingOverlay: {
              variant: 'circular-progress',
              noRowsVariant: 'circular-progress',
            },
            baseIconButton: {
              size: 'small',
            },
          }}
        />
      </Box>
      <Box sx={{ width: '100%' }}>
        <Stack direction="row" justifyContent="right" spacing={2} sx={{ width: '100%' }}>
          <Box sx={{ fontSize: '16px', pt: 1 }}>
            <strong>Total Yearly Consumable Cost:</strong> ${(yearlyConsumablesData?.consumableLineItems || []).length > 0 ? toUSCurrency(((yearlyConsumablesData?.consumableLineItems.reduce((sum, item) => sum + (item.totalCost || 0), 0)) || 0) / 2000) : '0.00'}
          </Box>
        </Stack>
      </Box>
      <Divider sx={{ mb: 3, mt: 1 }} />
      <Box sx={{ height: '27vh', width: '100%' }}>
        <DataGrid
          density="compact"
          label="Batch Consumables"
          rows={batchConsumablesRowsState.rows}
          columns={batchConsumableCostColumns}
          pagination
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          disableRowSelectionOnClick
          loading={batchConsumablesLoading}
          initialState={initialState}
          showToolbar
          pageSizeOptions={[INITIAL_PAGE_SIZE]}
          sx={{
            [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
              outline: 'transparent',
            },
            [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
            {
              outline: 'none',
            },
            [`& .${gridClasses.row}:hover`]: {
              cursor: 'pointer',
            }
          }}
          slotProps={{
            loadingOverlay: {
              variant: 'circular-progress',
              noRowsVariant: 'circular-progress',
            },
            baseIconButton: {
              size: 'small',
            },
          }}
        />
      </Box>
      <Box sx={{ width: '100%' }}>
        <Stack direction="row" justifyContent="right" spacing={2} sx={{ width: '100%' }}>
          <Box sx={{ fontSize: '16px', pt: 1 }}>
            <strong>Total Batch Cost:</strong> ${(batchConsumablesData?.consumableLineItems || []).length > 0 ? toUSCurrency(batchConsumablesData?.consumableLineItems.reduce((sum, item) => sum + (item.totalBatchCost || 0), 0)) : '0.00'}
          </Box>
        </Stack>
      </Box>
      <Divider sx={{ mb: 3, mt: 1 }} />
      <Stack direction="row" spacing={2} justifyContent="space-between">
        <Box></Box>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={handleEdit}
        >
          Edit
        </Button>
      </Stack>
    </>;
  }, [batchConsumableCostColumns, batchConsumablesData?.consumableLineItems, batchConsumablesError, batchConsumablesLoading, batchConsumablesRowsState.rows, filterModel, handleEdit, handleFilterModelChange, handlePaginationModelChange, handleSortModelChange, initialState, paginationModel, sortModel, yearlyConsumableCostColumns, yearlyConsumablesData?.consumableLineItems, yearlyConsumablesError, yearlyConsumablesLoading, yearlyConsumablesRowsState.rows]);

  return (
    <PageContainer
      title={'Consumables'}
    >
      <Box sx={{ flex: 1, width: '100%' }}>{renderShow}</Box>
    </PageContainer>
  );
}
